import { Select, Loader } from "@mantine/core"
import { useState, useMemo } from "react"

import { BudgetsResponses } from "@costory/types/endpoints/budgets"
import { CostType, Currency } from "@costory/types/prisma-client"

import { FormatedTd } from "@costory/front/pages/Budgets/components/FormatedTd"
import { SectionHead } from "@costory/front/pages/Budgets/components/SectionHead"
import { mediumGrayBorder } from "@costory/front/pages/Budgets/consts"
import { sumYTDOrNull } from "@costory/front/pages/Budgets/utils"
import { useBusinessMetricValueQuery } from "@costory/front/queries/businessMetrics"

interface BusinessMetricRowProps {
  virtualDimensionId: string
  virtualDimensionValue: string
  startDate: string
  formData: BudgetsResponses.BudgetFormData
  currency: Currency
  costType: CostType
}

export const BusinessMetricRow = ({
  virtualDimensionId,
  virtualDimensionValue,
  startDate,
  formData,
  currency,
  costType,
}: BusinessMetricRowProps) => {
  const [businessMetricId, setBusinessMetricId] = useState<string>("")

  const handleBusinessMetricChange = (value: string | null) => {
    setBusinessMetricId(value ?? "")
  }

  const businessMetricsSelectItems = useMemo(() => {
    return formData.availableBusinessMetrics.map((metric) => ({
      value: metric.id,
      label: metric.name,
    }))
  }, [formData.availableBusinessMetrics])

  const { data, isPending } = useBusinessMetricValueQuery({
    metricId: businessMetricId,
    virtualDimensionValueId: virtualDimensionId,
    startDate,
    currency,
    costType,
  })

  const vdimCosts = data?.costs[virtualDimensionValue]
  const bm = vdimCosts?.map((el) => ({
    cost: el.businessMetric,
    date: el.date,
  }))
  const businessMetric = bm
    ? bm.map((el) => el.cost)
    : (new Array(12).fill(null) as (number | null)[])
  const metric = vdimCosts?.map((el) => ({
    cost: el.metric,
    date: el.date,
  }))
    ? vdimCosts.map((el) => el.metric)
    : (new Array(12).fill(null) as (number | null)[])

  const businessMetricYtd = sumYTDOrNull(businessMetric)
  const metricYtd = sumYTDOrNull(metric)

  return (
    <>
      <tr>
        <th
          rowSpan={2}
          colSpan={2}
          style={{ borderRight: mediumGrayBorder, padding: 4, width: 150 }}
        >
          <Select
            variant="unstyled"
            leftSection={isPending ? <Loader size="sm" /> : undefined}
            data={businessMetricsSelectItems}
            value={businessMetricId === "" ? null : businessMetricId}
            onChange={handleBusinessMetricChange}
            placeholder="Select a business metric"
          />
        </th>
        <SectionHead name="Metric value" />
        {businessMetric.map((cost, index) => (
          <FormatedTd
            key={index}
            isBusinessMetric
            style={{
              borderRight: index === 11 ? mediumGrayBorder : undefined,
            }}
            data={cost}
          />
        ))}
        <FormatedTd
          isBusinessMetric
          style={{ borderRight: mediumGrayBorder }}
          data={businessMetricYtd}
        />
        <FormatedTd style={{ borderRight: mediumGrayBorder }} />
      </tr>
      <tr>
        <SectionHead name="Actual/Metric" />
        {metric.map((cost, index) => (
          <FormatedTd
            currency={currency}
            key={index}
            isBusinessMetric
            style={{
              borderRight: index === 11 ? mediumGrayBorder : undefined,
            }}
            data={cost}
          />
        ))}
        <FormatedTd
          currency={currency}
          isBusinessMetric
          style={{ borderRight: mediumGrayBorder }}
          data={metricYtd}
        />
        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          data={null}
          currency={currency}
        />
      </tr>
    </>
  )
}
